import { getCookie } from "cookies-next";
import api from "@/lib/api";

const VALID_EXTENSIONS = ["docx", "pdf", "png", "jpeg", "jpg", "mp4", "mov"];

export function getFileExtension(filename: string): string {
  const idx = filename.lastIndexOf(".");
  return idx >= 0 ? filename.slice(idx + 1).toLowerCase() : "";
}

export function isAllowedDocumentExtension(ext: string): boolean {
  return VALID_EXTENSIONS.includes(ext.toLowerCase());
}

export function normalizeContentType(ext: string, contentType: string): string {
  if (ext.toLowerCase() === "mov") return "video/quicktime";
  return contentType;
}

/**
 * DSP/USP match `document_location` as the S3 object key stored at create time
 * (e.g. `resident_incident_event_document/2026/07/16/….pdf`), not a full HTTPS URL.
 */
export function toStoredDocumentLocation(
  location: string | null | undefined,
): string {
  const trimmed = String(location ?? "").trim();
  if (!trimmed) return "";
  if (!/^https?:\/\//i.test(trimmed)) return trimmed;
  try {
    const pathname = new URL(trimmed).pathname.replace(/^\/+/, "");
    return pathname ? decodeURIComponent(pathname) : trimmed;
  } catch {
    return trimmed;
  }
}

export function readFileAsBase64Payload(file: File): Promise<{
  base64: string;
  contentType: string;
  extension: string;
}> {
  return new Promise((resolve, reject) => {
    const ext = getFileExtension(file.name);
    if (!isAllowedDocumentExtension(ext)) {
      reject(
        new Error(
          "Allowed only PNG or .JPEG or .JPG or .DOCX or .MP4 or .MOV or .PDF",
        ),
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const parts = result.split(/;base64,(.+)/);
      const base64 = parts[1] ?? result;
      resolve({
        base64,
        contentType: normalizeContentType(ext, file.type),
        extension: ext,
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/** Same presigner flow as grievance / form-entry document downloads. */
export async function downloadDocumentViaPresigner(
  s3BucketPath: string,
  fileName = "document",
): Promise<void> {
  const path = s3BucketPath.trim();
  if (!path) {
    throw new Error("No document path available for download");
  }

  const companyId = getCookie("company_id");
  if (!companyId) {
    throw new Error("Missing company ID");
  }

  const response = await api.post("/v1/presigner_generator/generate", {
    p_company_id: companyId,
    p_s3_bucket_path: path,
  });

  const presignedUrl = String(response.data?.file_url ?? "").trim();
  if (presignedUrl) {
    const link = document.createElement("a");
    link.href = presignedUrl;
    link.download = fileName || "document";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  window.open(path, "_blank", "noopener,noreferrer");
}

export function viewerUrlForDocument(
  fileType: string | null | undefined,
  s3Url: string,
): string {
  const ft = (fileType ?? "").toLowerCase();
  if (ft === "pdf") return `${s3Url}#navpanes=0`;
  if (ft === "doc") {
    return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(s3Url)}`;
  }
  return s3Url;
}
